/**
 * Constants and Enums
 * 
 * This file contains all constant values used throughout the application.
 * These match the Retell AI API enums and provide a single source of truth.
 * 
 * Benefits:
 * - Prevents typos (use CALL_STATUS.ENDED instead of "ended")
 * - Easy to update if API changes
 * - Autocomplete support in IDE
 * - Type safety
 */

// ============================================================================
// CALL STATUS
// ============================================================================
/**
 * Status of a call in its lifecycle
 * Maps to Retell API's call_status enum
 */
export const CALL_STATUS = {
  REGISTERED: 'registered',     // Call ID issued, starting to make a call
  NOT_CONNECTED: 'not_connected', // Call failed to connect
  ONGOING: 'ongoing',           // Call is currently active
  ENDED: 'ended',               // Call has completed (user or agent hung up)
  ERROR: 'error',               // Call encountered an error
};

/**
 * Human-readable labels for call status
 * Used in UI dropdowns and badges
 */
export const CALL_STATUS_LABELS = {
  [CALL_STATUS.REGISTERED]: 'Registered',
  [CALL_STATUS.NOT_CONNECTED]: 'Not Connected',
  [CALL_STATUS.ONGOING]: 'Ongoing',
  [CALL_STATUS.ENDED]: 'Ended',
  [CALL_STATUS.ERROR]: 'Error',
};

// ============================================================================
// CALL TYPES
// ============================================================================
/**
 * Type of call (web-based or phone)
 * Maps to Retell API's call_type enum
 */
export const CALL_TYPE = {
  WEB_CALL: 'web_call',       // Browser-based call
  PHONE_CALL: 'phone_call',   // Traditional phone call
};

export const CALL_TYPE_LABELS = {
  [CALL_TYPE.WEB_CALL]: 'Web Call',
  [CALL_TYPE.PHONE_CALL]: 'Phone Call',
};

// ============================================================================
// CALL DIRECTION
// ============================================================================
/**
 * Direction of the call
 * Maps to Retell API's direction enum
 */
export const CALL_DIRECTION = {
  INBOUND: 'inbound',   // Customer called the restaurant
  OUTBOUND: 'outbound', // Location called the customer
};

export const CALL_DIRECTION_LABELS = {
  [CALL_DIRECTION.INBOUND]: 'Inbound',
  [CALL_DIRECTION.OUTBOUND]: 'Outbound',
};

// ============================================================================
// USER SENTIMENT
// ============================================================================
/**
 * Sentiment analysis of the user during the call
 * Maps to Retell API's user_sentiment enum
 */
export const USER_SENTIMENT = {
  POSITIVE: 'Positive',   // User was happy/satisfied
  NEGATIVE: 'Negative',   // User was upset/frustrated
  NEUTRAL: 'Neutral',     // User was neither positive nor negative
  UNKNOWN: 'Unknown',     // Sentiment could not be determined
};

export const USER_SENTIMENT_LABELS = {
  [USER_SENTIMENT.POSITIVE]: 'Positive',
  [USER_SENTIMENT.NEGATIVE]: 'Negative',
  [USER_SENTIMENT.NEUTRAL]: 'Neutral',
  [USER_SENTIMENT.UNKNOWN]: 'Unknown',
};

// ============================================================================
// DISCONNECTION REASONS
// ============================================================================
/**
 * Reason why a call ended
 * Maps to Retell API's disconnection_reason enum
 * 
 * Categories:
 * - Normal endings (user_hangup, agent_hangup, call_transfer)
 * - Voicemail (voicemail_reached)
 * - System limits (inactivity, max_duration_reached, concurrency_limit_reached)
 * - Dial failures (dial_busy, dial_failed, dial_no_answer)
 * - Errors (error_*)
 */
export const DISCONNECTION_REASON = {
  // Normal endings
  USER_HANGUP: 'user_hangup',
  AGENT_HANGUP: 'agent_hangup',
  CALL_TRANSFER: 'call_transfer',
  
  // Voicemail
  VOICEMAIL_REACHED: 'voicemail_reached',
  
  // System limits
  INACTIVITY: 'inactivity',
  MAX_DURATION_REACHED: 'max_duration_reached',
  CONCURRENCY_LIMIT_REACHED: 'concurrency_limit_reached',
  NO_VALID_PAYMENT: 'no_valid_payment',
  
  // Spam/fraud detection
  SCAM_DETECTED: 'scam_detected',
  MARKED_AS_SPAM: 'marked_as_spam',
  USER_DECLINED: 'user_declined',
  
  // Dial failures
  DIAL_BUSY: 'dial_busy',
  DIAL_FAILED: 'dial_failed',
  DIAL_NO_ANSWER: 'dial_no_answer',
  INVALID_DESTINATION: 'invalid_destination',
  
  // Telephony provider issues
  TELEPHONY_PROVIDER_PERMISSION_DENIED: 'telephony_provider_permission_denied',
  TELEPHONY_PROVIDER_UNAVAILABLE: 'telephony_provider_unavailable',
  SIP_ROUTING_ERROR: 'sip_routing_error',
  
  // Errors
  ERROR_LLM_WEBSOCKET_OPEN: 'error_llm_websocket_open',
  ERROR_LLM_WEBSOCKET_LOST_CONNECTION: 'error_llm_websocket_lost_connection',
  ERROR_LLM_WEBSOCKET_RUNTIME: 'error_llm_websocket_runtime',
  ERROR_LLM_WEBSOCKET_CORRUPT_PAYLOAD: 'error_llm_websocket_corrupt_payload',
  ERROR_NO_AUDIO_RECEIVED: 'error_no_audio_received',
  ERROR_ASR: 'error_asr',
  ERROR_RETELL: 'error_retell',
  ERROR_UNKNOWN: 'error_unknown',
  ERROR_USER_NOT_JOINED: 'error_user_not_joined',
  REGISTERED_CALL_TIMEOUT: 'registered_call_timeout',
};

export const DISCONNECTION_REASON_LABELS = {
  [DISCONNECTION_REASON.USER_HANGUP]: 'User Hung Up',
  [DISCONNECTION_REASON.AGENT_HANGUP]: 'Agent Hung Up',
  [DISCONNECTION_REASON.CALL_TRANSFER]: 'Call Transferred',
  [DISCONNECTION_REASON.VOICEMAIL_REACHED]: 'Voicemail Reached',
  [DISCONNECTION_REASON.INACTIVITY]: 'Inactivity',
  [DISCONNECTION_REASON.MAX_DURATION_REACHED]: 'Max Duration Reached',
  [DISCONNECTION_REASON.CONCURRENCY_LIMIT_REACHED]: 'Concurrency Limit',
  [DISCONNECTION_REASON.NO_VALID_PAYMENT]: 'No Valid Payment',
  [DISCONNECTION_REASON.SCAM_DETECTED]: 'Scam Detected',
  [DISCONNECTION_REASON.MARKED_AS_SPAM]: 'Marked as Spam',
  [DISCONNECTION_REASON.USER_DECLINED]: 'User Declined',
  [DISCONNECTION_REASON.DIAL_BUSY]: 'Line Busy',
  [DISCONNECTION_REASON.DIAL_FAILED]: 'Dial Failed',
  [DISCONNECTION_REASON.DIAL_NO_ANSWER]: 'No Answer',
  [DISCONNECTION_REASON.INVALID_DESTINATION]: 'Invalid Number',
  [DISCONNECTION_REASON.TELEPHONY_PROVIDER_PERMISSION_DENIED]: 'Provider Permission Denied',
  [DISCONNECTION_REASON.TELEPHONY_PROVIDER_UNAVAILABLE]: 'Provider Unavailable',
  [DISCONNECTION_REASON.SIP_ROUTING_ERROR]: 'Routing Error',
  [DISCONNECTION_REASON.ERROR_LLM_WEBSOCKET_OPEN]: 'LLM Connection Error',
  [DISCONNECTION_REASON.ERROR_LLM_WEBSOCKET_LOST_CONNECTION]: 'LLM Connection Lost',
  [DISCONNECTION_REASON.ERROR_LLM_WEBSOCKET_RUNTIME]: 'LLM Runtime Error',
  [DISCONNECTION_REASON.ERROR_LLM_WEBSOCKET_CORRUPT_PAYLOAD]: 'LLM Corrupt Payload',
  [DISCONNECTION_REASON.ERROR_NO_AUDIO_RECEIVED]: 'No Audio Received',
  [DISCONNECTION_REASON.ERROR_ASR]: 'Speech Recognition Error',
  [DISCONNECTION_REASON.ERROR_RETELL]: 'Retell Error',
  [DISCONNECTION_REASON.ERROR_UNKNOWN]: 'Unknown Error',
  [DISCONNECTION_REASON.ERROR_USER_NOT_JOINED]: 'User Not Joined',
  [DISCONNECTION_REASON.REGISTERED_CALL_TIMEOUT]: 'Call Timeout',
};

// ============================================================================
// FILTER OPTIONS
// ============================================================================
/**
 * Options for filter dropdowns in the UI
 * Each array contains objects with value and label for <select> elements
 */

export const CALL_STATUS_OPTIONS = Object.keys(CALL_STATUS).map(key => ({
  value: CALL_STATUS[key],
  label: CALL_STATUS_LABELS[CALL_STATUS[key]],
}));

export const CALL_TYPE_OPTIONS = Object.keys(CALL_TYPE).map(key => ({
  value: CALL_TYPE[key],
  label: CALL_TYPE_LABELS[CALL_TYPE[key]],
}));

export const CALL_DIRECTION_OPTIONS = Object.keys(CALL_DIRECTION).map(key => ({
  value: CALL_DIRECTION[key],
  label: CALL_DIRECTION_LABELS[CALL_DIRECTION[key]],
}));

export const USER_SENTIMENT_OPTIONS = Object.keys(USER_SENTIMENT).map(key => ({
  value: USER_SENTIMENT[key],
  label: USER_SENTIMENT_LABELS[USER_SENTIMENT[key]],
}));

// ============================================================================
// DATE RANGE PRESETS
// ============================================================================
/**
 * Preset date ranges for quick filtering
 * Matches the Retell dashboard UI (Today, Last 7 days, etc.)
 */
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  LAST_7_DAYS: 'last_7_days',
  LAST_4_WEEKS: 'last_4_weeks',
  LAST_3_MONTHS: 'last_3_months',
  WEEK_TO_DATE: 'week_to_date',
  MONTH_TO_DATE: 'month_to_date',
  YEAR_TO_DATE: 'year_to_date',
  ALL_TIME: 'all_time',
  CUSTOM: 'custom',
};

export const DATE_RANGE_PRESET_LABELS = {
  [DATE_RANGE_PRESETS.TODAY]: 'Today',
  [DATE_RANGE_PRESETS.LAST_7_DAYS]: 'Last 7 days',
  [DATE_RANGE_PRESETS.LAST_4_WEEKS]: 'Last 4 weeks',
  [DATE_RANGE_PRESETS.LAST_3_MONTHS]: 'Last 3 months',
  [DATE_RANGE_PRESETS.WEEK_TO_DATE]: 'Week to date',
  [DATE_RANGE_PRESETS.MONTH_TO_DATE]: 'Month to date',
  [DATE_RANGE_PRESETS.YEAR_TO_DATE]: 'Year to date',
  [DATE_RANGE_PRESETS.ALL_TIME]: 'All time',
  [DATE_RANGE_PRESETS.CUSTOM]: 'Custom',
};

// ============================================================================
// PAGINATION
// ============================================================================
/**
 * Pagination constants
 * Matches Retell API limits
 */
export const PAGINATION = {
  DEFAULT_LIMIT: 50,      // Default number of calls per page
  MAX_LIMIT: 1000,        // Maximum allowed by Retell API
  PAGE_SIZE_OPTIONS: [25, 50, 100], // Options for "items per page" dropdown
};

// ============================================================================
// API ENDPOINTS
// ============================================================================
/**
 * API endpoint paths
 * Note: Base URL comes from environment variable
 */
export const API_ENDPOINTS = {
  // Auth endpoints (your backend)
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  ME: '/auth/me',
  REFRESH: '/auth/refresh',
  
  // Call endpoints (proxied to Retell)
  LIST_CALLS: '/api/calls/list',
  GET_CALL: '/api/calls/:id',
};

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================
/**
 * Keys for localStorage
 * Centralized to prevent typos
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'yapn_auth_token',
  REFRESH_TOKEN: 'yapn_refresh_token',
  USER: 'yapn_user',
  RESTAURANT: 'yapn_restaurant',
};

